import React, { useEffect, useState } from "react";
import { Tooltip } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { Link, useNavigate } from "react-router-dom";
import PaginationComponent from "../../../Components/PaginationComponent";
import toast from "react-hot-toast";
import saleService from "../../../Services/saleService";
import doctorService from "../../../Services/doctorService";
import patientService from "../../../Services/patientService";
import PatientReport from "../PatientReport";
import { pdf } from "@react-pdf/renderer";

const Patients = () => {
  const navigate = useNavigate();

  const [searchData, setSearchData] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [paginatedData, setPaginatedData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [patients, setPatients] = useState([]);
  const [patientsMaster, setPatientsMaster] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);

 
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);

        const [salesRes, doctorRes, patientRes] = await Promise.all([
          saleService.fetchAllSale(),
          doctorService.fetchAllDoctors(),
          patientService.fetchAllPatients(),
        ]);

        const sales = salesRes.sales || [];
        const doctorsData = doctorRes.doctors || [];
        const patientsData = patientRes.patients || [];

        setDoctors(doctorsData);
        setPatientsMaster(patientsData);

    
        const merged = sales.map((sale) => {
          const doctor = doctorsData.find((d) => d.id === sale.doc_id);
          const patient = patientsData.find(
            (p) => p.patient_id === sale.patient_id
          );

          return {
            ...sale,
            doctor_name: doctor?.name || sale.doctor_name || "",
            pmdc_no: doctor?.pmdc_no || "",
            qualification: doctor?.qualification || "",
            specialization: doctor?.specialization || "",
            full_name: patient?.full_name || sale.full_name || "",
            contact_number:
              patient?.contact_number || sale.contact_number || "",
            CNIC: patient?.CNIC || sale.CNIC || "",
            address: patient?.address || sale.address || "",
            age: patient?.age || sale.age || "",
            date_of_birth: patient?.date_of_birth || sale.date_of_birth || "",
            gender: patient?.gender || sale.gender || "",
            history: sale.history || "",
            diagnosis: sale.diagnosis || "",
          };
        });

        setPatients(merged);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        toast.error("Failed to load data");
      }
    };

    fetchAllData();
  }, []);

  
  useEffect(() => {
    const filtered =
      patients.filter(
        (item) =>
          item?.full_name?.toLowerCase().includes(searchData.toLowerCase()) ||
          item?.CNIC?.toLowerCase().includes(searchData.toLowerCase())
      ) || [];

    setFilteredData(filtered);
    setCurrentPage(1);
  }, [searchData, patients]);

  
  useEffect(() => {
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    setPaginatedData(filteredData.slice(start, end));
  }, [currentPage, filteredData]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

 
  const handlePrint = async (row) => {
    try {
      const patientPayload = {
        patient_name: row.full_name,
        doctor_name: row.doctor_name || "",
        pmdc_no: row.pmdc_no || "",
        patient_id: row.patient_id,
        sale_unique_id: row.sale_unique_id || "",
        sale_date: row.date,
        qualification: row.qualification || "",
        specialization: row.specialization || "",
        age: row.age || "",
        date_of_birth: row.date_of_birth || "",
        gender: row.gender || "",
        history: row.history || "",
        diagnosis: row.diagnosis || "",
      };

      const doc = <PatientReport patient={patientPayload} />;
      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);

      const newWindow = window.open(url, "_blank");
      if (newWindow) {
        newWindow.onload = () => {
          newWindow.focus();
          newWindow.print();
        };
      }
    } catch (error) {
      console.error(error);
      toast.error("Print failed");
    }
  };

  return (
    <div className="p-3 sm:p-6">
     
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between bg-white border border-gray-200 rounded-xl px-4 sm:px-6 py-4 mb-5 shadow-sm">
        <div>
          <h1 className="text-[16px] sm:text-[17px] font-[500] text-gray-800">
            Patients Table
          </h1>
          <p className="text-[12px] text-gray-400 mt-0.5">
            {filteredData.length} records found
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
         
          <div className="relative">
            <SearchIcon
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
              style={{ fontSize: 16 }}
            />
            <input
              type="search"
              placeholder="Search by name or CNIC..."
              value={searchData}
              onChange={(e) => setSearchData(e.target.value)}
              className="pl-8 pr-3 py-[7px] w-full sm:w-[230px] text-[13px] bg-gray-50 border border-gray-200 rounded-lg"
            />
          </div>

          <Link to="AddEdit" className="w-full sm:w-auto">
            <button className="w-full sm:w-auto bg-[#2F6DB4] hover:bg-[#255A8C] text-white text-[13px] px-4 py-[7px] rounded-lg">
              + Enter Patient
            </button>
          </Link>
        </div>
      </div>

    
      <div className="hidden md:block bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead>
              <tr className="bg-[#215187] text-[11px] uppercase text-white">
                <th className="py-3 px-4 text-left">Sale No.</th>
                <th className="py-3 px-4 text-left">Unique ID</th>
                <th className="py-3 px-4 text-left">Patient No.</th>
                <th className="py-3 px-4 text-left">Check-up Date</th>
                <th className="py-3 px-4 text-left">Patient Name</th>
                <th className="py-3 px-4 text-left">Contact</th>
                <th className="py-3 px-4 text-center">CNIC</th>
                <th className="py-3 px-4 text-center">Address</th>
                <th className="py-3 px-4 text-center">Action</th>
              </tr>
            </thead>

            <tbody>
              {paginatedData.map((row) => (
                <tr
                  key={row.sale_id}
                  className="border-t hover:bg-gray-50 text-[13px]"
                >
                  <td className="py-3 px-4">{row.sale_id}</td>
                  <td className="py-3 px-4 font-mono text-[12px] text-blue-600">
                    {row.sale_unique_id || "—"}
                  </td>
                  <td className="py-3 px-4">{row.patient_id}</td>
                  <td className="py-3 px-4">{formatDate(row.date)}</td>
                  <td className="py-3 px-4 font-[500]">{row.full_name}</td>
                  <td className="py-3 px-4">{row.contact_number}</td>
                  <td className="py-3 px-4 text-center">{row.CNIC}</td>
                  <td className="py-3 px-4 text-center">{row.address}</td>
                  <td className="py-3 px-4 text-center">
                    <button
                      onClick={() => handlePrint(row)}
                      className="bg-blue-50 text-blue-600 text-[12px] px-3 py-1 rounded-md hover:bg-blue-100"
                    >
                      Print
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

     
      <div className="flex flex-col gap-3 md:hidden">
        {paginatedData.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl px-6 py-10 text-center text-[13px] text-gray-400 shadow-sm">
            No records found.
          </div>
        ) : (
          paginatedData.map((row) => (
            <div
              key={row.sale_id}
              className="bg-white border border-gray-200 rounded-xl px-4 py-4 shadow-sm"
            >
              
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="text-[15px] font-[600] text-gray-800">
                    {row.full_name}
                  </p>
                  <p className="text-[12px] text-gray-400 mt-0.5">
                    {formatDate(row.date)}
                  </p>
                </div>
                <button
                  onClick={() => handlePrint(row)}
                  className="bg-blue-50 text-blue-600 text-[12px] px-3 py-1.5 rounded-md hover:bg-blue-100 shrink-0 ml-2"
                >
                  Print
                </button>
              </div>

             
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-[12px]">
                <div>
                  <span className="text-gray-400 block">Sale No.</span>
                  <span className="text-gray-700 font-[500]">{row.sale_id}</span>
                </div>
                <div>
                  <span className="text-gray-400 block">Unique ID</span>
                  <span className="text-gray-700 font-[500] font-mono">
                    {row.sale_unique_id || "—"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-400 block">Patient No.</span>
                  <span className="text-gray-700 font-[500]">
                    {row.patient_id}
                  </span>
                </div>
                <div>
                  <span className="text-gray-400 block">Contact</span>
                  <span className="text-gray-700 font-[500]">
                    {row.contact_number || "—"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-400 block">CNIC</span>
                  <span className="text-gray-700 font-[500]">
                    {row.CNIC || "—"}
                  </span>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-400 block">Address</span>
                  <span className="text-gray-700 font-[500]">
                    {row.address || "—"}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <PaginationComponent
        filteredData={filteredData}
        setPaginatedData={setPaginatedData}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
      />
    </div>
  );
};

export default Patients;